import { generateUnifiedCableSVG } from './unifiedSvgGenerator';
import { generateSKU } from './skuService';

/**
 * Customer SVG Export Service
 * Provides functionality for customers to download their cable diagrams as SVG files
 */

/**
 * Generate and download an SVG file for the customer's cable configuration
 * @param {Object} config - The cable configuration object
 * @param {string} filename - Optional custom filename (without extension)
 * @returns {Promise<void>}
 */
export async function exportCableSVG(config, filename = null) {
  try {
    console.log('🖼️ Generating customer SVG export for config:', config);
    
    // Generate SKU for the config
    const sku = generateSKU(config);
    
    // Generate the SVG content using the unified SVG generator
    const configWithSku = { ...config, sku };
    const svgContent = await generateUnifiedCableSVG(configWithSku);
    
    // Use the SKU we already generated
    const defaultFilename = `NLFX_Cable_${sku}_${Date.now()}`;
    const finalFilename = filename || defaultFilename;
    
    // Create a Blob with the SVG content
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${finalFilename}.svg`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('✅ Customer SVG export completed:', finalFilename);
    
    return {
      success: true,
      filename: `${finalFilename}.svg`,
      sku
    };
    
  } catch (error) {
    console.error('❌ Error exporting customer SVG:', error);
    throw new Error('Failed to export SVG diagram');
  }
}

/**
 * Generate SVG content as a string (for preview or other uses)
 * @param {Object} config - The cable configuration object
 * @returns {string} SVG content as string
 */
export async function generateCableSVGString(config) {
  try {
    return await generateUnifiedCableSVG(config);
  } catch (error) {
    console.error('❌ Error generating SVG string:', error);
    throw new Error('Failed to generate SVG content');
  }
}

/**
 * Get a data URL for the SVG (useful for embedding in other contexts)
 * @param {Object} config - The cable configuration object
 * @returns {string} Data URL for the SVG
 */
export async function getCableSVGDataURL(config) {
  try {
    const svgContent = await generateUnifiedCableSVG(config);
    const encoded = encodeURIComponent(svgContent);
    return `data:image/svg+xml;charset=utf-8,${encoded}`;
  } catch (error) {
    console.error('❌ Error generating SVG data URL:', error);
    throw new Error('Failed to generate SVG data URL');
  }
}

/**
 * Validate if a cable configuration can generate an SVG
 * @param {Object} config - The cable configuration object
 * @returns {boolean} True if SVG can be generated
 */
export function canGenerateSVG(config) {
  if (!config) return false;
  
  // Check if it's a complex cable type that supports SVG generation
  const complexCableTypes = ['hybrid', 'zip', 'zip-audio-audio', 'wye', 'hybrid-power-audio-audio'];
  
  if (complexCableTypes.includes(config.style)) {
    // For complex cables, check if hybridConnectors exist and have connectors
    return config.hybridConnectors && 
           config.hybridConnectors.connectors && 
           Object.keys(config.hybridConnectors.connectors).length > 0;
  }
  
  // For simple end-to-end cables, check if basic connectors exist
  if (config.style === 'end-to-end') {
    return config.connectors && 
           config.connectors.endA && 
           config.connectors.endB && 
           config.length && 
           config.length > 0;
  }
  
  // For PSHM cables, check if they have the required connectors
  if (config.hybridConnectors && config.hybridConnectors.connectors) {
    const connectors = config.hybridConnectors.connectors;
    const hasPSHMConnectors = connectors.G && connectors.H && connectors.I && connectors.J;
    if (hasPSHMConnectors) {
      return true;
    }
  }
  
  return false;
} 